import React, { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Loader2, Mail, Bell, BarChart3, Send, AlertTriangle, Clock } from "lucide-react";
import { toast } from "sonner";

export default function Notifications() {
  const { data, isLoading, refetch } = useQuery({
    queryKey: ["email-log"],
    queryFn: async () => (await api.get("/admin/email-log")).data,
  });
  const [sending, setSending] = useState(null);

  const send = async (kind) => {
    setSending(kind);
    try {
      const { data: res } = await api.post("/admin/send-emails", { kind });
      toast.success(`${res.sent} email(s) ${res.test_mode ? "logged (test mode)" : "sent"}`);
      refetch();
    } catch {
      toast.error("Failed to send.");
    } finally {
      setSending(null);
    }
  };

  const sendExpiry = async () => {
    setSending("expiry");
    try {
      const { data: res } = await api.post("/admin/send-expiry-reminders");
      toast.success(res.sent ? `${res.sent} expiry reminder(s) ${res.test_mode ? "logged (test mode)" : "sent"}` : "No students are expiring soon");
      refetch();
    } catch {
      toast.error("Failed to send.");
    } finally {
      setSending(null);
    }
  };

  const testMode = data?.test_mode;
  const logs = data?.logs || [];

  return (
    <div className="space-y-6" data-testid="notifications-page">
      <div>
        <p className="text-xs font-mono uppercase tracking-[0.2em] text-muted-foreground mb-1">Admin Tools</p>
        <h1 className="font-heading text-3xl sm:text-4xl font-black tracking-tighter flex items-center gap-3">
          <Mail className="w-7 h-7 text-primary" /> Notifications
        </h1>
        <p className="text-muted-foreground mt-1">Send reminders and weekly performance summaries to all students.</p>
      </div>

      {testMode && (
        <div className="flex items-start gap-3 p-4 rounded-md border border-chart-4/40 bg-chart-4/5" data-testid="test-mode-banner">
          <AlertTriangle className="w-5 h-5 text-chart-4 shrink-0 mt-0.5" />
          <div>
            <p className="font-semibold text-sm">Test mode active</p>
            <p className="text-sm text-muted-foreground">No Resend API key is configured, so emails are logged instead of delivered. Add a <span className="font-mono">RESEND_API_KEY</span> to send real emails.</p>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="p-6 rounded-md border border-border bg-card">
          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4"><Bell className="w-5 h-5 text-primary" /></div>
          <h3 className="font-heading font-bold text-lg mb-1">Practice Reminder</h3>
          <p className="text-sm text-muted-foreground mb-6">Nudge students who need to keep their prep momentum going.</p>
          <Button className="rounded-md gap-2" onClick={() => send("reminder")} disabled={sending} data-testid="send-reminder">
            {sending === "reminder" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Reminders</>}
          </Button>
        </div>
        <div className="p-6 rounded-md border border-border bg-card">
          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4"><BarChart3 className="w-5 h-5 text-primary" /></div>
          <h3 className="font-heading font-bold text-lg mb-1">Weekly Summary</h3>
          <p className="text-sm text-muted-foreground mb-6">Send a performance digest (auto-sent every Monday 9 AM).</p>
          <Button className="rounded-md gap-2" onClick={() => send("summary")} disabled={sending} data-testid="send-summary">
            {sending === "summary" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Summaries</>}
          </Button>
        </div>
        <div className="p-6 rounded-md border border-border bg-card">
          <div className="w-10 h-10 rounded-md bg-primary/10 flex items-center justify-center mb-4"><Clock className="w-5 h-5 text-primary" /></div>
          <h3 className="font-heading font-bold text-lg mb-1">Expiry Reminders</h3>
          <p className="text-sm text-muted-foreground mb-6">Email members whose access ends within 7 days so they can renew (auto-sent daily 9:30 AM).</p>
          <Button className="rounded-md gap-2" onClick={sendExpiry} disabled={sending} data-testid="send-expiry">
            {sending === "expiry" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Send className="w-4 h-4" /> Send Expiry Reminders</>}
          </Button>
        </div>
      </div>

      <div className="p-6 rounded-md border border-border bg-card">
        <h3 className="font-heading font-bold text-lg mb-4">Email Log</h3>
        {isLoading ? (
          <div className="flex justify-center py-8"><Loader2 className="w-6 h-6 animate-spin text-primary" /></div>
        ) : logs.length === 0 ? (
          <p className="text-sm text-muted-foreground">No emails sent yet.</p>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm" data-testid="email-log-table">
              <thead>
                <tr className="text-left text-muted-foreground font-mono text-xs uppercase tracking-wider border-b border-border">
                  <th className="py-2 pr-4">To</th><th className="py-2 pr-4">Subject</th><th className="py-2 pr-4">Status</th><th className="py-2">Sent</th>
                </tr>
              </thead>
              <tbody>
                {logs.map((l) => (
                  <tr key={l.id} className="border-b border-border last:border-0">
                    <td className="py-2.5 pr-4">{l.to}</td>
                    <td className="py-2.5 pr-4 text-muted-foreground truncate max-w-[200px]">{l.subject}</td>
                    <td className="py-2.5 pr-4"><span className={`font-mono text-xs px-2 py-0.5 rounded ${l.status === "sent" ? "bg-success/15 text-success" : l.status === "error" ? "bg-destructive/15 text-destructive" : "bg-secondary"}`}>{l.status}</span></td>
                    <td className="py-2.5 text-muted-foreground font-mono text-xs">{l.sent_at?.slice(0, 16).replace("T", " ")}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
